import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "./lib/supabase";

type PlanningRow = {
  worker_id: string;
  worker_name: string;
  worker_kind: "employee" | "contractor";
  item_id: string;
  item_kind: "mission" | "shift" | "leave";
  starts_at: string;
  ends_at: string;
  title: string;
  detail: string | null;
  status: string;
};

const mondayOf = (value: Date) => {
  const result = new Date(value);
  result.setHours(12, 0, 0, 0);
  result.setDate(result.getDate() - ((result.getDay() + 6) % 7));
  return result;
};

const dayKey = (value: string) =>
  new Intl.DateTimeFormat("fr-CA", {
    timeZone: "Europe/Paris",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(value));

const dayLabel = (value: Date) =>
  new Intl.DateTimeFormat("fr-FR", {
    timeZone: "Europe/Paris",
    weekday: "short",
    day: "2-digit",
    month: "short",
  }).format(value);

const hourLabel = (value: string) =>
  new Intl.DateTimeFormat("fr-FR", {
    timeZone: "Europe/Paris",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));

const statusLabel: Record<string, string> = {
  pending: "À confirmer",
  proposed: "Proposée",
  confirmed: "Confirmée",
  assigned: "Affectée",
  in_progress: "En cours",
  completed: "Terminée",
  approved: "Validée",
  published: "Publié",
};

export function TeamPlanningReadOnly() {
  const [weekCursor, setWeekCursor] = useState(() => mondayOf(new Date()));
  const [rows, setRows] = useState<PlanningRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const weekStart = useMemo(() => mondayOf(weekCursor), [weekCursor]);
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, index) => {
      const day = new Date(weekStart);
      day.setDate(day.getDate() + index);
      return day;
    }),
    [weekStart],
  );
  const load = useCallback(async () => {
    if (!supabase) return;
    setLoading(true);
    setMessage("");
    const result = await supabase.rpc("get_team_planning_week", {
      p_week_start: dayKey(weekStart.toISOString()),
    });
    setLoading(false);
    if (result.error) {
      setMessage(result.error.message);
      setRows([]);
      return;
    }
    setRows((result.data || []) as PlanningRow[]);
  }, [weekStart]);
  useEffect(() => { void load(); }, [load]);
  const people = useMemo(() => {
    const grouped = new Map<string, { id: string; name: string; kind: PlanningRow["worker_kind"]; rows: PlanningRow[] }>();
    rows.forEach((row) => {
      const current = grouped.get(row.worker_id) || {
        id: row.worker_id,
        name: row.worker_name,
        kind: row.worker_kind,
        rows: [],
      };
      current.rows.push(row);
      grouped.set(row.worker_id, current);
    });
    return [...grouped.values()].sort((left, right) => left.name.localeCompare(right.name, "fr"));
  }, [rows]);
  const moveWeek = (delta: number) => {
    const next = new Date(weekStart);
    next.setDate(next.getDate() + delta * 7);
    setWeekCursor(next);
  };
  return (
    <section className="panel team-planning-readonly" data-testid="team-planning-readonly">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Responsabilité d’équipe</span>
          <h1>Planning des employés</h1>
          <p>Votre agenda personnel reste inchangé. Cette vue complémentaire permet d’anticiper la semaine de l’équipe sans exposer les salaires, tarifs ni dossiers RH confidentiels.</p>
        </div>
        <span className="status-badge success">Lecture opérationnelle</span>
      </div>
      <div className="hr-week-toolbar">
        <button type="button" className="secondary" onClick={() => moveWeek(-1)}>←</button>
        <strong>Semaine du {dayLabel(weekStart)}</strong>
        <button type="button" className="secondary" onClick={() => setWeekCursor(mondayOf(new Date()))}>Aujourd’hui</button>
        <button type="button" className="secondary" onClick={() => moveWeek(1)}>→</button>
      </div>
      {loading ? <p>Chargement du planning de l’équipe…</p> : message ? (
        <p className="notice error">{message}</p>
      ) : !people.length ? (
        <p className="empty">Aucun élément planifié cette semaine.</p>
      ) : (
        <div className="team-week-board">
          {people.map((person) => (
            <article className="team-week-person" key={person.id}>
              <header>
                <div><strong>{person.name}</strong><small>{person.kind === "employee" ? "Employé" : "Sous-traitant"}</small></div>
                <span>{person.rows.length} élément(s)</span>
              </header>
              <div className="team-week-days">
                {days.map((day) => {
                  const key = dayKey(day.toISOString());
                  const dayRows = person.rows.filter((row) => dayKey(row.starts_at) === key);
                  return (
                    <section key={key} className={dayRows.length ? "has-items" : ""}>
                      <strong>{dayLabel(day)}</strong>
                      {dayRows.length ? dayRows.map((row) => (
                        <div className={`team-planning-item kind-${row.item_kind}`} key={`${row.item_kind}-${row.item_id}`}>
                          <time>{row.item_kind === "leave" ? "Journée" : `${hourLabel(row.starts_at)}–${hourLabel(row.ends_at)}`}</time>
                          <span><b>{row.title}</b>{row.detail && <small>{row.detail}</small>}</span>
                          <em>{statusLabel[row.status] || row.status}</em>
                        </div>
                      )) : <small className="empty">Libre</small>}
                    </section>
                  );
                })}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
